export interface SandboxEnvironmentContext {
  mode: string;
  deploymentProfile: string;
  claimBoundary: string;
}

/**
 * GET /v1/session/effective-access returns `environment.claimBoundary` as a
 * raw enum wire value (e.g. "sandbox_no_live_funds") on most pages today —
 * only a few page-specific probes have a human sentence recorded instead
 * (ALL-4921). The frontend must never show a raw wire code as page copy, so
 * known codes are mapped to the same human sentence the fixture default
 * uses. An unrecognized code still renders (never silently blanked — that
 * would hide a real, if unmapped, claim-boundary signal), but every known
 * code the backend actually sends today is covered here.
 */
const CLAIM_BOUNDARY_COPY: Record<string, string> = {
  sandbox_no_live_funds: "Shared Alloy-hosted sandbox. Non-production, non-custodial, no live funds.",
};

function claimBoundaryCopy(raw: string): string {
  // A plain bracket lookup walks the prototype chain, so a wire value that
  // happens to be an Object member name ("constructor", "__proto__",
  // "toString", …) matched an inherited member and this function — typed as
  // returning `string` — returned a function or an object instead. Only an own
  // entry counts as a known code; anything else is an unrecognized code and
  // renders as the raw value it was.
  return Object.prototype.hasOwnProperty.call(CLAIM_BOUNDARY_COPY, raw) ? CLAIM_BOUNDARY_COPY[raw] : raw;
}

export interface SandboxOrganizationContext {
  id: string;
  label: string;
  tenantId: string;
  trustDomainId: string;
  roleSummary: string;
  supportReference: string;
}

export interface SandboxMembershipOption {
  id: string;
  label: string;
  selected: boolean;
}

export interface SandboxInviteState {
  recipient: string;
  roleBundle: string;
  status: string;
  expiresIn: string;
  supportReference: string;
}

export interface SandboxDeniedState {
  reasonCode: string;
  title: string;
  detail: string;
  remediation: string;
  supportReference: string;
}

export interface ExperienceContract {
  environment: SandboxEnvironmentContext;
  activeOrganization: SandboxOrganizationContext;
  memberships: SandboxMembershipOption[];
  invites: SandboxInviteState[];
  deniedState: SandboxDeniedState | null;
  grantedScopes: string[];
  grantedPermissions: string[];
  source: "api" | "fixture" | "unknown";
}

/**
 * Neutral placeholder used as the store's initial value, on transport errors,
 * and to fill fields the API did not return. It must never contain invented
 * organization, tenant or customer names: a fabricated org label that reaches
 * the shell looks exactly like real customer context (this is how
 * "Customer B Sandbox" once appeared on dev). Anything unknown renders as an
 * em dash, so a missing value is visibly missing.
 */
export const EXPERIENCE_CONTRACT_UNKNOWN: ExperienceContract = {
  source: "unknown",
  // No environment is claimed until the API names one: an empty mode renders
  // no environment marker rather than an invented "Sandbox".
  environment: { mode: "", deploymentProfile: "", claimBoundary: "" },
  activeOrganization: {
    id: "",
    label: "—",
    tenantId: "",
    trustDomainId: "",
    roleSummary: "—",
    supportReference: "",
  },
  memberships: [],
  invites: [],
  deniedState: null,
  grantedScopes: [],
  grantedPermissions: [],
};

export function normalizeExperienceContract(input: unknown, source: "api" | "fixture" | "unknown" = "api"): ExperienceContract {
  const data = objectRecord(input) ?? {};
  const principal = objectRecord(data.principalContext) || objectRecord(data.context) || {};
  const environment = objectRecord(data.environment) || {};
  const active = objectRecord(data.activeOrganization) || objectRecord(data.activeMembership) || objectRecord(data.organization) || {};
  // Never fall back to fixture customer names for fields the API omitted.
  const fallback = EXPERIENCE_CONTRACT_UNKNOWN;
  const activeOrganization = {
    id: boundedId(active.id || active.organizationId || principal.organizationId, fallback.activeOrganization.id),
    label: boundedText(active.label || active.displayName || active.organizationName, fallback.activeOrganization.label),
    tenantId: boundedId(active.tenantId || principal.tenantId, fallback.activeOrganization.tenantId),
    trustDomainId: boundedId(active.trustDomainId || principal.trustDomainId, fallback.activeOrganization.trustDomainId),
    roleSummary: boundedText(active.roleSummary || roleSummary(active.roles || data.roles), fallback.activeOrganization.roleSummary),
    supportReference: boundedText(active.supportReference || data.supportReference, fallback.activeOrganization.supportReference),
  };

  return {
    source,
    environment: {
      mode: boundedId(environment.mode || data.environmentMode || principal.environment || data.environment, fallback.environment.mode),
      deploymentProfile: boundedId(
        environment.deploymentProfile || data.deploymentProfile || principal.deploymentProfile,
        fallback.environment.deploymentProfile,
      ),
      claimBoundary: claimBoundaryCopy(
        boundedText(environment.claimBoundary || data.claimBoundary, fallback.environment.claimBoundary),
      ),
    },
    activeOrganization,
    memberships: normalizeMemberships(data.memberships, activeOrganization),
    invites: normalizeInvites(data.invites),
    deniedState: normalizeDeniedState(data.deniedState || data.roleDenied),
    grantedScopes: normalizeStringList(data.grantedScopes),
    grantedPermissions: normalizeStringList(data.grantedPermissions),
  };
}

function normalizeMemberships(input: unknown, active: SandboxOrganizationContext): SandboxMembershipOption[] {
  const items = Array.isArray(input) ? input : EXPERIENCE_CONTRACT_UNKNOWN.memberships;
  return items.slice(0, 6).map((raw) => {
    const item = objectRecord(raw) || {};
    const id = boundedId(item.id || item.organizationId, active.id);
    return {
      id,
      label: boundedText(item.label || item.displayName || item.organizationName, active.label),
      selected: item.selected === true || id === active.id,
    };
  });
}

function normalizeInvites(input: unknown): SandboxInviteState[] {
  const items = Array.isArray(input) ? input : EXPERIENCE_CONTRACT_UNKNOWN.invites;
  return items.slice(0, 4).map((raw) => {
    const item = objectRecord(raw) || {};
    return {
      recipient: boundedText(item.recipient || item.email, "pending admin"),
      roleBundle: boundedText(item.roleBundle || item.role, "organization_admin_starter"),
      status: boundedId(item.status, "invited"),
      expiresIn: boundedText(item.expiresIn || item.expires_in, "24h"),
      supportReference: boundedText(item.supportReference, "support-ref-invite"),
    };
  });
}

function normalizeDeniedState(input: unknown): SandboxDeniedState | null {
  if (input == null) return null;
  const item = objectRecord(input);
  if (!item) return null;
  const reasonCode = typeof item.reason_code === "string" ? item.reason_code : typeof item.reasonCode === "string" ? item.reasonCode : "";
  if (!reasonCode.trim()) return null;
  return {
    reasonCode: boundedId(reasonCode, "unknown"),
    title: boundedText(item.title, "Access restricted"),
    detail: boundedText(item.detail, ""),
    remediation: boundedText(item.remediation, ""),
    supportReference: boundedText(item.supportReference, ""),
  };
}

function normalizeStringList(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input.filter((v): v is string => typeof v === "string" && v.trim().length > 0).slice(0, 100);
}

function roleSummary(input: unknown): string {
  return Array.isArray(input) ? input.slice(0, 3).map(String).join(", ") : "";
}

function objectRecord(input: unknown): Record<string, unknown> | null {
  return input && typeof input === "object" && !Array.isArray(input) ? input as Record<string, unknown> : null;
}

function boundedText(input: unknown, fallback: string): string {
  if (typeof input !== "string") return fallback;
  const value = input.trim();
  return value ? value.slice(0, 140) : fallback;
}

function boundedId(input: unknown, fallback: string): string {
  if (typeof input !== "string") return fallback;
  const value = input.trim();
  return /^[a-zA-Z0-9_.:-]{2,96}$/.test(value) ? value : fallback;
}
